/**
 * Справочник причин отмены и дисквалификации регистрации.
 *
 * Бэк коды не проверяет и не переводит: админ отправляет код, участнику он
 * возвращается как есть в `participant_registration.reason`. Поэтому подпись на
 * языке участника берёт фронт, а справочник общий — им пользуются и модалки
 * администратора, и страница участника.
 *
 * Ключи подписей — полными литералами, а не склейкой с кодом: склеенный ключ не
 * находится грепом, и чистильщик мёртвых ключей удалит его как неиспользуемый.
 * Подписи лежат в `common.json`: namespace общий для обеих ролей.
 */
export const REGISTRATION_REASON_LABEL_KEYS = {
  incorrect_participant_data: 'registration_reason.incorrect_participant_data',
  incorrect_grade: 'registration_reason.incorrect_grade',
  duplicate_registration: 'registration_reason.duplicate_registration',
  participant_request: 'registration_reason.participant_request',
  cancelled_by_administrator: 'registration_reason.cancelled_by_administrator',
  venue_removed: 'registration_reason.venue_removed',
  venue_location_changed: 'registration_reason.venue_location_changed',
  participant_location_changed: 'registration_reason.participant_location_changed',
  rule_violation: 'registration_reason.rule_violation',
  proctoring_violation: 'registration_reason.proctoring_violation',
  outside_help: 'registration_reason.outside_help',
  impersonation: 'registration_reason.impersonation',
} as const

export type RegistrationReasonCode = keyof typeof REGISTRATION_REASON_LABEL_KEYS

/** Коды отмены, которые админ выбирает из списка, — в порядке показа (список
 *  заказчика, 12.10). Третий пункт списка — «Другая причина» — кодом не
 *  является: см. `CUSTOM_CANCEL_REASON`. Старые коды (`incorrect_grade`,
 *  `participant_request`, `cancelled_by_administrator`) остаются в
 *  `REGISTRATION_REASON_LABEL_KEYS`: у уже отменённых регистраций их видит участник. */
export const CANCEL_REASONS = [
  'duplicate_registration',
  'incorrect_participant_data',
] as const satisfies readonly RegistrationReasonCode[]
export type CancelReason = (typeof CANCEL_REASONS)[number]

/** Пункт выбора «Другая причина». Только значение селекта: на бэк уходит текст
 *  админа (1–64 символа), а не эта строка, — поэтому её нет среди кодов
 *  справочника подписей. Текст участник видит как есть, на языке админа. */
export const CUSTOM_CANCEL_REASON = 'custom' as const

/** Автоотмены бэка: админ их выбрать не может, но участник видит подпись. */
export const AUTO_CANCEL_REASONS = [
  'venue_removed',
  'venue_location_changed',
  'participant_location_changed',
] as const satisfies readonly RegistrationReasonCode[]

/** Что админ выбирает при дисквалификации. */
export const DISQUALIFY_REASONS = [
  'rule_violation',
  'proctoring_violation',
  'outside_help',
  'impersonation',
] as const satisfies readonly RegistrationReasonCode[]
export type DisqualifyReason = (typeof DISQUALIFY_REASONS)[number]

/** Ключ общей фразы, когда причины нет или она нам незнакома. */
export const REGISTRATION_REASON_UNKNOWN_KEY = 'registration_reason.unknown'

// Код бэка — латиница/цифры минимум с одним «_». Одно слово («test») — это уже
// текст админа, а не код.
const MACHINE_CODE = /^[a-z0-9]+(?:_[a-z0-9]+)+$/

export function isRegistrationReasonCode(value: string): value is RegistrationReasonCode {
  return Object.hasOwn(REGISTRATION_REASON_LABEL_KEYS, value)
}

/**
 * Подпись причины для участника.
 *
 * — код из справочника → его перевод;
 * — незнакомая строка, похожая на код (строчная латиница/цифры минимум с одним
 *   «_», `^[a-z0-9]+(?:_[a-z0-9]+)+$`) → общая фраза: показывать `some_new_code`
 *   человеку нельзя. Одно слово без «_» («test») — текст админа, показывается как есть;
 * — всё остальное — свободный текст (причина «Другая» при отмене, у старых дисквалификаций) — дословно;
 * — пусто → общая фраза.
 *
 * `t` должен находить ключи из `common.json`.
 */
export function registrationReasonLabel(
  reason: string | null | undefined,
  t: (key: string) => string
): string {
  const value = reason?.trim()
  if (!value) return t(REGISTRATION_REASON_UNKNOWN_KEY)
  if (isRegistrationReasonCode(value)) return t(REGISTRATION_REASON_LABEL_KEYS[value])
  if (MACHINE_CODE.test(value)) return t(REGISTRATION_REASON_UNKNOWN_KEY)
  return value
}
